import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowUp, ArrowDown, Minus, TrendingUp } from 'lucide-react';

/**
 * Progress-over-time line chart powered by
 * GET /api/student-strategy/progress-timeline.
 *
 * Three series:
 *   - Prelims accuracy (%)
 *   - Mains avg percentage
 *   - CSAT accuracy (%)
 *
 * Trends summarise last-2-weeks vs prior-2-weeks delta.
 */

const SERIES = [
  { key: 'prelimsAccuracy', name: 'Prelims', stroke: '#6366f1', trendKey: 'prelims_accuracy' },
  { key: 'mainsScore', name: 'Mains', stroke: '#10b981', trendKey: 'mains_score' },
  { key: 'csatAccuracy', name: 'CSAT', stroke: '#06b6d4', trendKey: 'csat_accuracy' },
];

const TrendBadge = ({ trend }) => {
  if (!trend) {
    return (
      <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500 border-slate-200" title="Need 4+ weeks of activity to compute a trend">
        <Minus className="w-3 h-3 mr-0.5" />
        building…
      </Badge>
    );
  }
  const { direction, delta } = trend;
  if (direction === 'improving') {
    return (
      <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
        <ArrowUp className="w-3 h-3 mr-0.5" />+{delta}%
      </Badge>
    );
  }
  if (direction === 'declining') {
    return (
      <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">
        <ArrowDown className="w-3 h-3 mr-0.5" />
        {delta}%
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">
      <Minus className="w-3 h-3 mr-0.5" />
      stable
    </Badge>
  );
};

export const ProgressTimelineChart = ({ data }) => {
  if (!data) return null;

  // Pull weekly numbers into the shape recharts expects
  const chartData = (data.timeline || []).map((wk) => ({
    week: formatWeek(wk.week_start),
    weekRaw: wk.week_start,
    prelimsAccuracy: wk.prelims?.accuracy,
    mainsScore: wk.mains?.avg_percentage,
    csatAccuracy: wk.csat?.accuracy,
  }));

  const hasAnyData = data.has_data;

  return (
    <Card className="border-0 shadow-md" data-testid="progress-timeline-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-indigo-500" />
            <h3 className="font-semibold text-slate-800">Progress Over Time</h3>
          </div>
          <span className="text-[10px] text-slate-400">
            Last {data.window_days} days · weekly
          </span>
        </div>

        {/* Trend strip */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {SERIES.map((s) => {
            const trend = data.trends?.[s.trendKey];
            return (
              <div key={s.key} className="flex items-center gap-1.5" data-testid={`trend-${s.trendKey}`}>
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ backgroundColor: s.stroke }}
                />
                <span className="text-xs font-medium text-slate-700">{s.name}</span>
                <TrendBadge trend={trend} />
              </div>
            );
          })}
        </div>

        {!hasAnyData ? (
          <div className="py-10 text-center text-sm text-slate-400">
            No practice activity in this window yet.
            <br />
            <span className="text-xs">Start practicing to see your trend.</span>
          </div>
        ) : (
          <div className="w-full h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#64748b' }} interval="preserveStartEnd" />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  contentStyle={{ fontSize: '12px', borderRadius: '6px' }}
                  formatter={(value, name) => (value == null ? ['—', name] : [`${value}%`, name])}
                />
                <Legend
                  wrapperStyle={{ fontSize: '11px' }}
                  iconType="circle"
                  iconSize={8}
                />
                {SERIES.map((s) => (
                  <Line
                    key={s.key}
                    type="monotone"
                    name={s.name}
                    dataKey={s.key}
                    stroke={s.stroke}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

function formatWeek(iso) {
  if (!iso) return '';
  // ISO YYYY-MM-DD → "Feb 10"
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
